<template>

    <div class="ui-popup-layer">
        <div ref="positioner" :style="positionerStyles">
            <slot :placement="display ? display.placement : null"></slot>
        </div>
    </div>

</template>

<script lang="ts">

import { Component, Prop, Vue } from 'vue-facing-decorator';
import { clone, debounce, isArray, isElement, isObject, isString } from 'lodash';

import ResizeObserver from 'resize-observer-polyfill';

import type { ComponentProps } from '@wittignl/js-core';
import { createComponentMixin } from '@wittignl/js-core';

import type { PopupDisplay, PopupOptions, PopupOverlayOptions, PopupPlacement, ResolvedPopupPlacement } from './common';
import { PopupPosition, PopupSide } from './common';

const DEFAULT_MARGIN = 5;
const DEFAULT_DISTANCE = 5;

const allSides = [PopupSide.RIGHT, PopupSide.LEFT, PopupSide.TOP, PopupSide.BOTTOM];
const allPositions = [PopupPosition.CENTER, PopupPosition.START, PopupPosition.END];

@Component({
    name: 'ui-popup-layer'
})
export default class PopupLayer extends Vue {

    @Prop({ type: Object })
    options: PopupOverlayOptions;

    display: PopupDisplay = null;

    updateDebounced: typeof this.updateDisplay
    resizeObserver: ResizeObserver
    scrollParents: (Node|Window)[]

    created() {

        this.updateDebounced = debounce(() => this.updateDisplay(), 100);
    }

    get tooltipOptions(): PopupOptions {

        return this.options?.tooltipOptions;
    }

    mounted() {

        this.updateDisplay();

        window.addEventListener('resize', this.updateDebounced);

        // Resize
        const $positioner = this.$refs.positioner as Element;
        this.resizeObserver = new ResizeObserver(entries => {

            for(const entry of entries){

                if(entry.target === $positioner){

                    this.updateDisplay();
                    break;
                }
            }
        });

        this.resizeObserver.observe($positioner);

        // Scroll parents
        this.scrollParents = [];

        if(this.tooltipOptions.anchor && isElement(this.tooltipOptions.anchor)){

            let element: Node = this.tooltipOptions.anchor as Node;

            for(; element && element !== document; element = element.parentNode){

                if(!(element instanceof Element)){
                    continue;
                }

                const style = getComputedStyle(element);
                const isScrollContainer = /(auto|scroll|overlay)/.test(style.overflow + style.overflowY + style.overflowX);

                if(isScrollContainer){

                    element.addEventListener('scroll', this.updateDebounced);
                    this.scrollParents.push(element);
                }
            }

            // Always add window
            if(this.scrollParents.indexOf(window) < 0){

                element.addEventListener('scroll', this.updateDebounced);
                this.scrollParents.push(window);
            }
        }
    }

    beforeUnmount() {

        window.removeEventListener('resize', this.updateDebounced);

        if(this.resizeObserver){
            this.resizeObserver.disconnect();
        }

        for(const element of this.scrollParents){
            element.removeEventListener('scroll', this.updateDebounced);
        }
    }

    get positionerStyles() {

        const display = this.display;

        if(!display){

            return {
                'position': 'absolute',
                'visibility': 'hidden'
            };
        }

        return {
            'position': 'absolute',
            'pointer-events': this.tooltipOptions.pointerEvents !== false ? 'auto' : 'none',
            'left': display ? `${display.position.x}px` : 0,
            'top': display ? `${display.position.y}px` : 0
        };
    }

    get margin() {

        return this.tooltipOptions.margin !== undefined ? this.tooltipOptions.margin : DEFAULT_MARGIN;
    }

    updateDisplay() {

        const $positioner = this.$refs.positioner as Element;
        const $popup = $positioner ? $positioner.firstElementChild : null;

        if(!$popup || !$popup.getBoundingClientRect){
            return null;
        }

        const anchorRect = this.getAnchorRect();
        const popupRect = $popup.getBoundingClientRect();
        if(popupRect.width <= 0 || popupRect.height <= 0){
            return;
        }

        if(!anchorRect){
            throw 'Invalid anchor provided for popup';
        }

        if(!popupRect){
            throw 'Unable to get rect for popup';
        }

        const placement = this.getPlacement(anchorRect, popupRect);
        const position = this.calculatePosition(placement, anchorRect, popupRect);

        this.display = {
            placement, position
        };
    }

    calculatePosition(placement: ResolvedPopupPlacement, anchorRect: DOMRect, popupRect: DOMRect) {

        const { side, position } = placement;

        const distance = this.tooltipOptions.distance !== undefined ? this.tooltipOptions.distance : DEFAULT_DISTANCE;

        let x = 0;
        let y = 0;

        switch(side){

            case PopupSide.LEFT:
                x = anchorRect.left - popupRect.width - distance;
                break;

            case PopupSide.RIGHT:
                x = anchorRect.left + anchorRect.width + distance;
                break;

            case PopupSide.TOP:
                y = anchorRect.top - popupRect.height - distance;
                break;

            case PopupSide.BOTTOM:
                y = anchorRect.top + anchorRect.height + distance;
                break;
        }

        const isVertical = [PopupSide.LEFT, PopupSide.RIGHT].indexOf(side) >= 0;

        switch(position){

            case PopupPosition.START:

                if(isVertical){
                    y = anchorRect.top;
                }
                else {
                    x = anchorRect.left;
                }
                break;

            case PopupPosition.END:

                if(isVertical){
                    y = (anchorRect.top + anchorRect.height) - popupRect.height;
                }
                else {
                    x = (anchorRect.left + anchorRect.width) - popupRect.width;
                }
                break;

            case PopupPosition.CENTER:

                if(isVertical){
                    y = (anchorRect.top + (anchorRect.height / 2.0)) - (popupRect.height / 2.0);
                }
                else {
                    x = (anchorRect.left + (anchorRect.width / 2.0)) - (popupRect.width / 2.0);
                }
                break;
        }

        if(this.tooltipOptions.container){

            const containerRect = this.tooltipOptions.container.getBoundingClientRect();

            x = Math.min(Math.max(x, containerRect.left), containerRect.left + containerRect.width);
            y = Math.min(Math.max(y, containerRect.top), (containerRect.top + containerRect.height) - popupRect.height);
        }

        return {
            x: Math.round(x),
            y: Math.round(y)
        };
    }

    getAnchorRect(): DOMRect {

        const anchor = this.tooltipOptions.anchor;
        let anchorRect = null;

        if(isElement(anchor)){
            anchorRect = (anchor as Element).getBoundingClientRect();
        }
        else if(isObject(anchor)){

            anchorRect = {
                width: 1,
                height: 1,
                ...anchor
            };
        }

        return anchorRect;
    }

    getPlacement(anchorRect: DOMRect, popupRect: DOMRect): ResolvedPopupPlacement {

        const options = this.getResolvedPlacementOptions();

        const placement: ResolvedPopupPlacement = {
            side: PopupSide.BOTTOM,
            position: PopupPosition.CENTER
        };

        const rawRect: DOMRect = this.$el.getBoundingClientRect();

        const containerRect = {
            left: rawRect.left,
            top: rawRect.top,
            width: rawRect.width,
            height: rawRect.height
        };

        containerRect.left += this.margin;
        containerRect.top += this.margin;
        containerRect.width -= this.margin * 2.0;
        containerRect.height -= this.margin * 2.0;

        for(const side of options.side){

            let foundPlacement = false;

            for(const position of options.position){

                const pos = this.calculatePosition({ side, position }, anchorRect, popupRect);

                // Check if inside container
                if(pos.x >= containerRect.left &&
                    pos.y >= containerRect.top &&
                    pos.x + popupRect.width <= containerRect.left + containerRect.width &&
                    pos.y + popupRect.height <= containerRect.top + containerRect.height){

                    placement.side = side;
                    placement.position = position;

                    foundPlacement = true;
                    break;
                }
            }

            if(foundPlacement){
                break;
            }
        }

        return placement;
    }

    getResolvedPlacementOptions(): PopupPlacement {

        const option = this.tooltipOptions.placement;
        let resolved: any = {};

        if(isString(option)){

            const parts = option.split('-');

            resolved.side = (parts[0] || null) as PopupSide;

            if(parts.length > 1){
                resolved.position = (parts[1] || null) as PopupPosition
            }
        }
        else if(isArray(option)){

            resolved = { side: clone(option) };
        }
        else {

            resolved = clone(option || {});
        }

        if(resolved.side && isString(resolved.side)){
            resolved.side = resolved.side.split('|');
        }

        if(resolved.position && isString(resolved.position)){
            resolved.position = resolved.position.split('|');
        }

        if(!resolved.side || !resolved.side.length || resolved.side[0] === 'auto'){
            resolved.side = allSides;
        }

        if(!resolved.position || !resolved.position.length || resolved.position[0] === 'auto'){
            resolved.position = allPositions;
        }

        return resolved;
    }
}

export type PopupLayerComp = InstanceType<typeof PopupLayer>
export type PopupLayerProps = ComponentProps<PopupLayerComp>
export const popupLayerCompMixin = createComponentMixin(PopupLayer)

</script>
<style src="../../../scss/component/overlay/PopupLayer.scss" lang="scss"></style>